/**
 * The one empty / first-run state used by every data surface in the app.
 *
 * Rules (Task 38 empty-state audit):
 *   - a decorative glyph (`aria-hidden`) so screen readers hear only the copy;
 *   - a `<h*>` title that says what is missing, not "no data";
 *   - a description under ~80 characters that says what to do next;
 *   - an optional call to action pointing at the screen that fills the void;
 *   - `variant="filtered"` for "your filter matched nothing" — semantically a
 *     different message from "you have nothing yet", and it gets a
 *     `role="status"` so a live filter change is announced.
 */
import type { ReactNode } from 'react';

export type EmptyStateVariant = 'empty' | 'filtered' | 'unavailable';

export interface EmptyStateProps {
  title: string;
  description?: ReactNode;
  /** Decorative glyph; rendered inside an `aria-hidden` wrapper. */
  icon?: ReactNode;
  /** Primary call to action — a `<Link className="btn ...">` or `<button>`. */
  action?: ReactNode;
  /** Secondary affordance, e.g. "Clear filters". */
  secondaryAction?: ReactNode;
  variant?: EmptyStateVariant;
  /** Tighter padding for empty states nested inside a card or a table body. */
  isCompact?: boolean;
  /** Heading level, so the state does not break the page outline. Default 3. */
  headingLevel?: 2 | 3 | 4;
  className?: string;
  'data-testid'?: string;
}

const VARIANT_ICON: Record<EmptyStateVariant, string> = {
  empty: '＋',
  filtered: '⌕',
  unavailable: '⊘',
};

export function EmptyState({
  title,
  description,
  icon,
  action,
  secondaryAction,
  variant = 'empty',
  isCompact = false,
  headingLevel = 3,
  className,
  'data-testid': testId,
}: EmptyStateProps): JSX.Element {
  const Heading = `h${headingLevel}` as 'h2' | 'h3' | 'h4';
  const classes = [
    'empty-state',
    `empty-state--${variant}`,
    isCompact ? 'empty-state--compact' : null,
    className ?? null,
  ]
    .filter((value): value is string => value !== null)
    .join(' ');

  return (
    <div
      className={classes}
      data-testid={testId ?? 'empty-state'}
      data-variant={variant}
      role={variant === 'filtered' ? 'status' : undefined}
    >
      <span className="empty-state__icon" aria-hidden="true">
        {icon ?? VARIANT_ICON[variant]}
      </span>
      <Heading className="empty-state__title">{title}</Heading>
      {description === undefined ? null : <p className="empty-state__description">{description}</p>}
      {action === undefined && secondaryAction === undefined ? null : (
        <div className="empty-state__actions">
          {action}
          {secondaryAction}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------- copy */

export interface EmptyStateCopy {
  title: string;
  description: string;
  variant: EmptyStateVariant;
}

/**
 * Canonical copy for every empty surface in the product, keyed by surface id.
 * Centralised so wording stays consistent and the audit suite can assert that
 * each screen ships a defensible first-run message.
 */
export const EMPTY_STATE_COPY = {
  'play-archive': {
    title: 'No plays logged yet',
    description: 'Start entering plays from Play Entry when the game begins.',
    variant: 'empty',
  },
  'play-archive-filtered': {
    title: 'No plays match this filter',
    description: 'Try a different Offense / Defense / Special Teams filter or search text.',
    variant: 'filtered',
  },
  'play-archive-conflicts': {
    title: 'No conflicting plays',
    description: 'All submissions have been resolved or agree on their stats.',
    variant: 'empty',
  },
  highlight: {
    title: 'No plays logged yet',
    description: 'Start entering plays from Play Entry when the game begins.',
    variant: 'empty',
  },
  'stat-tracker': {
    title: 'No plays logged yet',
    description: 'Start entering plays from Play Entry when the game begins.',
    variant: 'empty',
  },
  roster: {
    title: 'No players on roster',
    description: 'Add your first player from the Roster Manager to begin.',
    variant: 'empty',
  },
  'roster-filtered': {
    title: 'No players match this filter',
    description: 'Try a different cohort or clear the search box.',
    variant: 'filtered',
  },
  playbook: {
    title: 'No options for this stat',
    description: 'Add a team option, or turn a stock option back on.',
    variant: 'empty',
  },
  events: {
    title: 'No games scheduled',
    description: 'Create your first game or practice to begin.',
    variant: 'empty',
  },
  'faq-search': {
    title: 'No FAQs match your search',
    description: 'Try different keywords or browse all FAQs.',
    variant: 'filtered',
  },
  docs: {
    title: 'No documentation available for your role',
    description: 'Check the FAQs or contact support.',
    variant: 'filtered',
  },
  'support-request': {
    title: 'Contact our support team',
    description: 'Tell us what you need help with. A screenshot is optional.',
    variant: 'empty',
  },
  video: {
    title: 'Video not available',
    description: "Your team hasn't configured a video provider, or video is coach-only.",
    variant: 'unavailable',
  },
} as const satisfies Record<string, EmptyStateCopy>;

export type EmptyStateKey = keyof typeof EMPTY_STATE_COPY;

/** `<EmptyState {...emptyStateCopy('roster')} action={…} />` */
export function emptyStateCopy(key: EmptyStateKey): EmptyStateCopy {
  return EMPTY_STATE_COPY[key];
}
